/* eslint-disable no-nested-ternary */
import { AppState, AppThunk } from "@Store/index";
import {
  filter_keys,
  getFilterParams,
  getSearchLink,
  hasRoomTypeFilter,
  withActiveRoomTypesMethod,
} from "./helper";
import fetchSearchData from "./fetchSearchData";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import fetchInventorisData, { fetchMoreInventoriesList } from "./fetchInventoriesData";
import { convertToFilterObj } from "../utils/formatFilterState";
import urlBuilder from "@Utils/urlBuilder";
import { isDemoWhitelabelSubdomain } from "constants/index";
import { getMetaSuccess } from "@App/appReducer";
import isValidMoveInMonth from "../utils/isValidMoveInMonth";
import config from "@Config/index";
import axios from "axios";
import {
  ROOM_TYPE_FILTER,
  ROOM_TYPE_SPECIFIC,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
} from "../components/FilterSection/data/filtersList";
import { metaTypesHas } from "../components/SearchList/helper";
import { COUNTRIES_WITH_MONTHLY_DURATION } from "constants/configConstants";
import { addLanguageToPath } from "@Utils/languageCore";
import { appendUniqueInventories } from "../utils/geoRadiusExpand";
import { RECOMMENDATIONS_MAX_ITEMS, RelaxationStep } from "../utils/relaxation";
import { nextApplicationOrder } from "../utils/filterApplicationOrder";
import {
  AppliedFilterSequenceItem,
  buildAppliedFilterSequenceFromQuery,
  updateAppliedFilterSequence,
} from "../utils/appliedFilterSequence";

export interface RecommendationsState {
  status: "idle" | "planning" | "ready" | "exhausted" | "hidden";
  /** Monotonic; a response whose id is no longer current is dropped (FR-31a). */
  requestId: number;
  plan: RelaxationStep[];
  currentStepIdx: number;
  currentPage: number;
  pageLoading: boolean;
  items: any[];
  /** inventory id → accepted step index, for card props and events. */
  stepOfItem: Record<string, number>;
  mainResultCount: number;
  /** Map view only: the section is on screen, so the map also pins its properties. */
  inView: boolean;
}

/** No more pages: keep what is rendered, or hide the section when nothing is. */
const finishRecommendations = (rec: RecommendationsState) => {
  rec.pageLoading = false;
  rec.status = rec.items.length ? "exhausted" : "hidden";
};

export const initialRecommendations: RecommendationsState = {
  status: "idle",
  requestId: 0,
  plan: [],
  currentStepIdx: 0,
  currentPage: 1,
  pageLoading: false,
  items: [],
  stepOfItem: {},
  mainResultCount: 0,
  inView: false,
};

interface SearchPageData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  region: any;
  error: string | null;
  canonical_name: string;
  filters: any;
  canonical_url: string;
  mobileMapData: {
    show: boolean;
    activeInv: string;
  };
  dynamicFilterCountData: {
    isLoading: boolean;
    data: any;
    params: any;
  };
  searchList: {
    readyStatus: "invalid" | "request" | "success" | "failure";
    items: any;
    error: null;
    page: 1;
    params: any;
    meta: any;
    next?: number | null;
  };
  regionList: {
    readyStatus: "invalid" | "request" | "success" | "failure";
    isCountry: boolean;
    result: any;
    stateList?: any;
    error: any;
  };
  filterUniversityList: {
    data: Array<any>;
    ancestorId: string;
    isLoading: boolean;
  };
  filterLocalityList: {
    data: Array<any>;
    ancestorId: string;
    isLoading: boolean;
  };
  metaSEO?: {
    canonical_url_suffix: string;
    description: string;
    keywords?: string[];
    ogImage?: string;
    schemaData?: Record<string, any>[];
    title_prefix?: string;
    url?: string;
  };
  nearbyData: {
    cities: Array<any>;
    areas: Array<any>;
    universities: Array<any>;
    inventories: Array<any>;
  };
  listingData: {
    cities: Array<any>;
    areas: Array<any>;
    universities: Array<any>;
  };
  trustpilotReviews: any[];
  fatFooterData: any;
  isAgentViewOpened: boolean;
  selectedInventories: Array<any>;
  customLocation: Record<string, any>;
  customPMGName: string;
  customPropertyName: string;
  whiteLabelPartnerId: any;
  whiteLabelPartnerData: any;
  showZones: boolean;
  agentViewZone: number;
  agentViewStatuses: string;
  loadingInventoryIds: string[];
  isRadiusExpanded: boolean;
  radiusExpandSplitIndex: number;
  /** Frozen `meta.count` from before expand — main header keeps showing this. */
  radiusExpandPrimaryCount: number | null;
  radiusExpandEnabled: boolean;
  /** Filter tokens in the order the user applied them (Redux only). See utils/filterTokens. */
  filterApplicationOrder: string[];
  recommendations: RecommendationsState;
  appliedFilterSequence: AppliedFilterSequenceItem[];
}

export const initialState: SearchPageData = {
  readyStatus: "invalid",
  region: null,
  error: null,
  canonical_name: "",
  canonical_url: "",
  dynamicFilterCountData: {
    isLoading: true,
    data: null,
    params: null,
  },
  filters: {},
  searchList: {
    readyStatus: "request",
    meta: null,
    items: [],
    error: null,
    page: 1,
    params: {},
  },
  regionList: {
    readyStatus: "invalid",
    isCountry: false,
    result: [],
    stateList: [],
    error: null,
  },
  filterUniversityList: {
    data: [],
    ancestorId: "",
    isLoading: true,
  },
  filterLocalityList: {
    data: [],
    ancestorId: "",
    isLoading: true,
  },
  metaSEO: {
    canonical_url_suffix: "",
    description: "",
    keywords: [],
    ogImage: "",
    schemaData: [],
    title_prefix: "",
    url: "",
  },
  mobileMapData: {
    show: false,
    activeInv: "",
  },
  nearbyData: {
    cities: [],
    areas: [],
    universities: [],
    inventories: [],
  },
  listingData: {
    cities: [],
    areas: [],
    universities: [],
  },
  trustpilotReviews: [],
  fatFooterData: {},
  customLocation: {},
  selectedInventories: [],
  isAgentViewOpened: false,
  customPMGName: "",
  customPropertyName: "",
  whiteLabelPartnerId: null,
  whiteLabelPartnerData: null,
  showZones: false,
  agentViewZone: 0,
  agentViewStatuses: "",
  loadingInventoryIds: [],
  isRadiusExpanded: false,
  radiusExpandSplitIndex: 0,
  radiusExpandPrimaryCount: null,
  radiusExpandEnabled: false,
  filterApplicationOrder: [],
  recommendations: initialRecommendations,
  appliedFilterSequence: [],
};

const searchPageData = createSlice({
  name: "searchPageData",
  initialState,
  reducers: {
    getSearchRequesting: (state: SearchPageData, { payload }: PayloadAction<any>) => {
      state.searchList = initialState.searchList;
      state.regionList = initialState.regionList;
      state.listingData = initialState.listingData;
      state.readyStatus = "request";
      state.region = initialState.region;
      state.filters = initialState.filters;
      state.appliedFilterSequence = [];
      state.canonical_name = payload;
      state.isRadiusExpanded = false;
      state.radiusExpandSplitIndex = 0;
      state.radiusExpandPrimaryCount = null;
      state.filterApplicationOrder = [];
      state.recommendations = initialRecommendations;

      if (__CLIENT__) {
        window.scrollTo(0, 0);
      }
    },
    getSearchSuccess: (state: SearchPageData, { payload }: PayloadAction<any>) => {
      state.readyStatus = "success";
      state.region = payload?.region;
      state.filters = payload?.filters || {};
      state.canonical_url = payload?.canonical_url;
    },
    updateNearbyData: (state, { payload }: PayloadAction<any>) => {
      state.nearbyData = payload;
    },
    updateListingData: (state, { payload }: PayloadAction<any>) => {
      state.listingData = payload;
    },
    updateFatFooterData: (state, { payload }: PayloadAction<any>) => {
      state.fatFooterData = payload;
    },
    updateTrustpilotReviews: (state, { payload }: PayloadAction<any[]>) => {
      state.trustpilotReviews = payload;
    },
    getSearchFailure: (state: SearchPageData, { payload }: PayloadAction<any>) => {
      state.readyStatus = "failure";
      state.error = JSON.stringify(payload);
    },
    getSearchListRequesting: (state: SearchPageData, { payload }: PayloadAction<any>) => {
      state.searchList.readyStatus = "request";
      state.searchList.page = payload?.page;
      // During backward navigation (skipScroll=true), preserve items if canonical_name hasn't changed
      // This prevents white screen flash on mobile devices when navigating back through filter history
      const isBackwardNavigation = payload?.skipScroll === true;
      const canonicalNameChanged = state.canonical_name !== payload?.canonical_name;

      // Clear items if:
      // 1. Canonical name changed (region changed) - always clear
      // 2. Forward navigation with forceFetch - clear to show loading state
      // During backward navigation, only clear if canonical_name changed to prevent white screen
      const shouldClearItems =
        canonicalNameChanged || (!isBackwardNavigation && payload.forceFetch);

      if (shouldClearItems) {
        state.searchList.items = [];
        state.searchList.next = null;
        state.searchList.params = {};
        state.searchList.meta = null;
        state.radiusExpandSplitIndex = 0;
        state.radiusExpandPrimaryCount = null;
        state.recommendations = initialRecommendations;
      }
      if (canonicalNameChanged) {
        state.isRadiusExpanded = false;
        state.radiusExpandSplitIndex = 0;
        state.radiusExpandPrimaryCount = null;
        state.filterApplicationOrder = [];
        state.recommendations = initialRecommendations;
      }
      state.canonical_name = payload?.canonical_name;
    },
    getSearchListSuccess: (state, { payload }: PayloadAction<any>) => {
      state.searchList.readyStatus = "success";
      state.searchList.items = payload?.appendResults
        ? appendUniqueInventories(state.searchList.items, payload?.result)
        : payload?.result;
      state.searchList.next = payload?.meta?.next;
      state.searchList.params = payload?.params;
      state.searchList.meta = payload?.meta;
      state.filters = payload?.filters || {};
      state.canonical_url = payload?.canonical_url;
      state.metaSEO = payload?.metaSEO || {};
      // Skip scrollTo during backward navigation (when skipScroll is true)
      // Use requestAnimationFrame to ensure proper painting before scrolling
      if (__CLIENT__ && !payload?.skipScroll && !payload?.appendResults) {
        // Use requestAnimationFrame to wait for next paint, then scroll
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0 });
        });
      }
      state.selectedInventories = [];
      state.customLocation = state.customLocation || {};
      state.customPropertyName = payload?.customPropertyName || state.customPropertyName || "";
    },
    updateSearchResultForAgentView: (state, { payload }: PayloadAction<any>) => {
      state.searchList.readyStatus = "success";
      state.searchList.items = payload.result;
      state.customLocation = state.customLocation || {};
      state.isAgentViewOpened = payload.isAgentViewOpened || state.isAgentViewOpened;
      // Agent view swaps the rendered set out from under the section (FR-47).
      if (state.isAgentViewOpened) state.recommendations = initialRecommendations;
    },
    gettSearchListSuccessPagination: (state, { payload }: PayloadAction<any>) => {
      state.searchList.readyStatus = "success";
      state.searchList.items = appendUniqueInventories(state.searchList.items, payload.data.result);
      state.searchList.next = payload?.data?.meta?.next;
      state.searchList.params = payload?.params;
    },
    getSearchListFailure: (state, { payload }: PayloadAction<string>) => {
      state.searchList.readyStatus = "failure";
      state.error = payload;
    },
    getRegionListSuccess: (state: SearchPageData, { payload }: PayloadAction<any>) => {
      state.regionList.readyStatus = "success";
      state.regionList.result = payload?.data?.data?.result;
      state.regionList.stateList = payload?.statesList?.data?.result;
      state.regionList.isCountry = payload?.isCountry;
    },
    getRegionListError: (state: SearchPageData, { payload }: PayloadAction<any>) => {
      state.regionList.readyStatus = "failure";
      state.regionList.error = payload?.error;
    },
    updateFilters: (state, { payload }: PayloadAction<any>) => {
      state.filters = payload;
    },
    setAppliedFilterSequence: (state, { payload }: PayloadAction<AppliedFilterSequenceItem[]>) => {
      state.appliedFilterSequence = payload;
    },
    updateDynamicFilterCountData: (state, { payload }: PayloadAction<any>) => {
      state.dynamicFilterCountData = payload;
    },
    setFilterUniversityList: (state, { payload }: PayloadAction<any>) => {
      state.filterUniversityList = payload;
    },
    setFilterLocalityList: (state, { payload }: PayloadAction<any>) => {
      state.filterLocalityList = payload;
    },
    showMobileMap: (state) => {
      state.mobileMapData = {
        show: true,
        activeInv: "",
      };
    },
    hideMobileMap: (state) => {
      state.mobileMapData = {
        show: false,
        activeInv: "",
      };
    },
    setMobileMapActiveInv: (state, { payload }: PayloadAction<any>) => {
      state.mobileMapData = {
        ...state.mobileMapData,
        activeInv: payload,
      };
    },
    updateSelectedInventories: (state, { payload }: PayloadAction<any>) => {
      state.selectedInventories = payload.selectedInventories;
    },
    updateCustomLocation: (state, { payload }: PayloadAction<any>) => {
      state.customLocation = payload.customLocation;
    },
    updateWhiteLabelProviderId: (state, { payload }: PayloadAction<any>) => {
      state.whiteLabelPartnerId = payload.whiteLabelPartnerId;
      state.whiteLabelPartnerData = payload.whiteLabelPartnerData;
    },
    updateCustomPMGName: (state, { payload }: PayloadAction<any>) => {
      state.customPMGName = payload.customPMGName;
    },
    removeCustomPropertyName: (state) => {
      state.customPropertyName = "";
    },
    updateShowZones: (state) => {
      state.showZones = !state.showZones;
    },
    updateAgentViewZone: (state, { payload }: PayloadAction<number>) => {
      state.agentViewZone = payload;
    },
    updateAgentViewStatuses: (state, { payload }: PayloadAction<string>) => {
      state.agentViewStatuses = payload;
    },
    updateReadyStatus: (state, { payload }: PayloadAction<any>) => {
      state.searchList.readyStatus = payload.readyStatus || state.searchList.readyStatus;
      if (payload?.forceFetch) state.searchList.items = [];
    },
    setLoadingInventoryIds: (state, { payload }: PayloadAction<string[]>) => {
      state.loadingInventoryIds = [...(state.loadingInventoryIds || []), ...payload];
    },
    clearLoadingInventoryIds: (state, { payload }: PayloadAction<string[]>) => {
      state.loadingInventoryIds = (state.loadingInventoryIds || []).filter(
        (id) => !payload.includes(id),
      );
    },
    setRadiusExpanded: (state, { payload }: PayloadAction<boolean>) => {
      if (payload) {
        state.isRadiusExpanded = true;
        state.radiusExpandSplitIndex = state.searchList.items?.length || 0;
        state.radiusExpandPrimaryCount =
          state.searchList.meta?.count ?? state.searchList.items?.length ?? 0;
      } else {
        state.isRadiusExpanded = false;
        state.radiusExpandSplitIndex = 0;
        state.radiusExpandPrimaryCount = null;
      }
    },
    setRadiusExpandEnabled: (state, { payload }: PayloadAction<boolean>) => {
      state.radiusExpandEnabled = payload;
    },
    setFilterApplicationOrder: (state, { payload }: PayloadAction<string[]>) => {
      state.filterApplicationOrder = payload || [];
    },
    resetRecommendations: (state) => {
      state.recommendations = initialRecommendations;
    },
    recommendationsPlanning: (state, { payload }: PayloadAction<number>) => {
      state.recommendations = { ...initialRecommendations, status: "planning", requestId: payload };
    },
    recommendationsPlanned: (
      state,
      { payload }: PayloadAction<{ plan: RelaxationStep[]; mainCount: number }>,
    ) => {
      const rec = state.recommendations;
      rec.plan = payload.plan;
      rec.mainResultCount = payload.mainCount;
      rec.status = payload.plan.length ? "ready" : "hidden";
    },
    setRecommendationsInView: (state, { payload }: PayloadAction<boolean>) => {
      state.recommendations.inView = payload;
    },
    recommendationsPageLoading: (state, { payload }: PayloadAction<number>) => {
      state.recommendations.requestId = payload;
      state.recommendations.pageLoading = true;
    },
    recommendationsPageLoaded: (
      state,
      { payload }: PayloadAction<{ items: any[]; next: number | null; stepIndex: number }>,
    ) => {
      const rec = state.recommendations;
      rec.pageLoading = false;
      const before = rec.items.length;
      // The sold-out tail step ignores the main filters, so it can repeat main-list cards.
      const mainIds = new Set((state.searchList.items || []).map((item: any) => String(item.id)));
      rec.items = appendUniqueInventories(
        rec.items,
        payload.items.filter((item: any) => !mainIds.has(String(item.id))),
      ).slice(0, RECOMMENDATIONS_MAX_ITEMS);
      rec.items.slice(before).forEach((item: any) => {
        rec.stepOfItem[String(item.id)] = payload.stepIndex;
      });
      if (typeof payload.next === "number" && payload.next > 0) {
        rec.currentPage = payload.next;
      } else {
        rec.currentStepIdx += 1;
        rec.currentPage = 1;
      }
      if (rec.currentStepIdx >= rec.plan.length || rec.items.length >= RECOMMENDATIONS_MAX_ITEMS) {
        finishRecommendations(rec);
      }
    },
    // A failed later page keeps what is shown (FR-33a); any other failure hides the section.
    recommendationsFailed: (
      state,
      { payload }: PayloadAction<{ keepItems?: boolean } | undefined>,
    ) => {
      if (!payload?.keepItems) state.recommendations.items = [];
      finishRecommendations(state.recommendations);
    },
  },
});

export default searchPageData.reducer;
export const {
  getSearchRequesting,
  getSearchSuccess,
  updateNearbyData,
  updateListingData,
  updateFatFooterData,
  updateTrustpilotReviews,
  getSearchFailure,
  getSearchListFailure,
  getSearchListRequesting,
  getSearchListSuccess,
  gettSearchListSuccessPagination,
  updateFilters,
  setAppliedFilterSequence,
  getRegionListSuccess,
  getRegionListError,
  updateDynamicFilterCountData,
  setFilterUniversityList,
  setFilterLocalityList,
  showMobileMap,
  hideMobileMap,
  setMobileMapActiveInv,
  updateSearchResultForAgentView,
  updateSelectedInventories,
  updateCustomLocation,
  updateWhiteLabelProviderId,
  updateCustomPMGName,
  removeCustomPropertyName,
  updateShowZones,
  updateAgentViewZone,
  updateAgentViewStatuses,
  updateReadyStatus,
  setLoadingInventoryIds,
  clearLoadingInventoryIds,
  setRadiusExpanded,
  setRadiusExpandEnabled,
  setFilterApplicationOrder,
  resetRecommendations,
  recommendationsPlanning,
  recommendationsPlanned,
  setRecommendationsInView,
  recommendationsPageLoading,
  recommendationsPageLoaded,
  recommendationsFailed,
} = searchPageData.actions;

export const applyFilter =
  (filters: any, clearUni = false, clearLocality = false, skipHistoryPush = false): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();

    // Set loading state to prevent white screen
    if (skipHistoryPush) {
      dispatch({ type: "SET_FILTER_NAVIGATION_LOADING", payload: true });
    }

    try {
      // Extract canonicalName from filters if present (for backward navigation)
      let canonicalNameFromFilters: string | null = null;
      if (skipHistoryPush && filters?._canonicalName) {
        canonicalNameFromFilters = filters._canonicalName;
        const cleanedFilters = Object.fromEntries(
          Object.entries(filters).filter(([k]) => k !== "_canonicalName"),
        );
        filters = cleanedFilters;
      }
      // Application order (Redux only): surviving tokens keep their slot, new ones append (FR-8).
      const applicationOrder = nextApplicationOrder(
        state.searchPageData.filterApplicationOrder,
        filters,
        state.searchPageData.filters,
      );

      // Any filter change after expand collapses back to the default geo radius so
      // the next fetch omits region_radius/locality_radius and the banner can return.
      if (state.searchPageData.isRadiusExpanded) {
        dispatch(setRadiusExpanded(false));
      }

      const filterObj = convertToFilterObj(filters);
      // update sort key based on cookies
      let sortKey = "";
      // const searchSortingKey = getCookie("search_sorting_key_v2");
      // const searchSortingKey50 = getCookie("search_sorting_key_v2_50");
      // const regionId = state.searchPageData.region?.id;
      // const regionParentId = state.searchPageData.region?.parent_id;
      // const rootId =
      //   state?.searchPageData?.region?.ancestors_short?.length > 0
      //     ? state.searchPageData.region.ancestors_short.slice(-1)[0].id
      //     : "";
      // const sortingAlgoV2RegionIds = SORTING_ALGO_V2_REGION_IDS;
      // const sortingAlgoV2RegionIds50 = SORTING_ALGO_V2_REGION_IDS_50;

      // Check if region is enrolled in sorting algorithm experiment
      // const isInV2RegionIds =
      //   sortingAlgoV2RegionIds.includes(Number(regionId)) ||
      //   sortingAlgoV2RegionIds.includes(Number(regionParentId));
      // const isInV2RegionIds50 =
      //   sortingAlgoV2RegionIds50.includes(Number(regionId)) ||
      //   sortingAlgoV2RegionIds50.includes(Number(regionParentId));
      // const shouldApplyV2Sorting =
      //   (rootId == 1 || rootId == 5 || isInV2RegionIds50) && searchSortingKey50 === "TG1";

      // Check if this is a nearby page
      // Use includes to match locality=near-by-{region-name} pattern
      const currentLocality = filters?.locality || state.searchPageData.filters?.locality;
      const isNearbyPage = currentLocality && String(currentLocality).includes("near-by");

      // Check if any filters are applied (excluding locality and sortKey)
      const hasActiveFilters = Object.keys(filters || {}).some(
        (key) =>
          key !== "locality" &&
          key !== "sortKey" &&
          filters[key] !== undefined &&
          filters[key] !== null &&
          filters[key] !== "",
      );

      // University filter specific checks
      let canonicalName = filters?.university
        ? filters.university
        : filters?.locality && !String(filters.locality).includes("near-by")
        ? filters.locality
        : state.searchPageData.canonical_name;

      // During backward navigation, use canonicalName from history state if available
      if (canonicalNameFromFilters) {
        canonicalName = canonicalNameFromFilters;
      }

      // If still no canonicalName and we're in backward navigation, extract from URL
      if (skipHistoryPush && !canonicalName && typeof window !== "undefined") {
        const pathMatch = window.location.pathname.match(/\/places\/search\/([^/]+)/);
        const [, extractedCanonicalName] = pathMatch || [];
        if (extractedCanonicalName) {
          canonicalName = extractedCanonicalName;
        }
      }

      // On nearby page, when clearing university or locality filters, keep the parent canonical name
      // to avoid staying on the university/locality page
      if (clearUni && state?.searchPageData?.region?.region_type == "establishment") {
        if (!isNearbyPage && !filters?.locality) {
          canonicalName = state?.searchPageData?.region?.parent_canonical_name ?? canonicalName;
        } else if (isNearbyPage) {
          // On nearby page, navigate to the parent region (the city)
          canonicalName = state?.searchPageData?.region?.parent_canonical_name ?? canonicalName;
        }
      }

      if (clearLocality && state?.searchPageData?.region?.region_type == "sublocality") {
        if (!isNearbyPage) {
          canonicalName = state?.searchPageData?.region?.parent_canonical_name ?? canonicalName;
        } else {
          // On nearby page, navigate to the parent region
          canonicalName = state?.searchPageData?.region?.parent_canonical_name ?? canonicalName;
        }
      }
      // NOW determine sort key after canonical name is corrected
      if (
        state.searchPageData.region?.region_type === "establishment" ||
        state.searchPageData.region?.region_type === "sublocality" ||
        ("locality" in state.searchPageData.filters && !isNearbyPage)
      ) {
        sortKey = "relevance_v0";
      } else if (isNearbyPage) {
        // For nearby page, use nearby_sorted_distance when no filters, relevance_v0 when filters applied
        sortKey = hasActiveFilters ? "relevance_v0" : "nearby_sorted_distance";
      } else {
        sortKey = "relevance_sorting_v2";
      }

      let newUrl = getSearchLink(canonicalName, filterObj);
      if (
        state.whitelabelData.isWhitelabel &&
        isDemoWhitelabelSubdomain(state.whitelabelData.subdomain)
      ) {
        newUrl = urlBuilder(newUrl, {
          params: { partner_id: state.whitelabelData.partner_uuid },
        });
      }

      const href =
        filters?.university || filters?.locality
          ? (() => {
              const { appData }: any = window.__INITIAL_STATE__ || {};
              const userLanguage = appData?.userLanguage || "en";
              const path = `/places/search/${canonicalName}`;
              return `${window.location.origin}${addLanguageToPath(path, userLanguage)}`;
            })()
          : window.location.href;

      // When navigating via history (skipHistoryPush=true), always update even if URL matches
      const shouldUpdate = skipHistoryPush || newUrl !== state.searchPageData.canonical_url;

      if (shouldUpdate) {
        // During backward navigation, don't scroll to top - let browser maintain scroll position
        if (!skipHistoryPush && __CLIENT__) {
          requestAnimationFrame(() => {
            window.scrollTo({ top: 0 });
          });
        }

        const storedSequence = state.searchPageData.appliedFilterSequence;
        // An empty sequence still has an on-screen order (the query string).
        // Seed from that before appending, or the new value is inserted in
        // group order and earlier pills jump ahead.
        const sequenceBase = storedSequence?.length
          ? storedSequence
          : buildAppliedFilterSequenceFromQuery(
              __CLIENT__
                ? Object.fromEntries(new URLSearchParams(window.location.search))
                : state.searchPageData.filters,
            );
        dispatch(setAppliedFilterSequence(updateAppliedFilterSequence(sequenceBase, filters)));
        dispatch(updateFilters(filters));

        // Determine if we need to fetch search data or just inventories
        const universityChanged = state.searchPageData.filters.university !== filters?.university;
        const localityChanged =
          state.searchPageData.filters.locality !== filters?.locality &&
          !(filters?.locality && String(filters.locality).includes("near-by"));
        const regionExists =
          state.searchPageData.region !== null && state.searchPageData.region !== undefined;
        const canonicalNameChanged = state.searchPageData.canonical_name !== canonicalName;

        // CRITICAL FIX: During back navigation, if canonical name hasn't changed and region exists,
        // just fetch inventories to avoid unnecessary region refetch that causes white screen
        const shouldFetchSearchData =
          universityChanged ||
          localityChanged ||
          !regionExists ||
          (skipHistoryPush && canonicalNameChanged);

        // CRITICAL FIX: If navigating back to the same canonical name, reuse existing region
        const canReuseRegion =
          skipHistoryPush &&
          !canonicalNameChanged &&
          regionExists &&
          !universityChanged &&
          !localityChanged;

        await dispatch(
          shouldFetchSearchData && !canReuseRegion
            ? fetchSearchData({
                canonical_name: canonicalName,
                filters: filterObj,
                href,
                forceFetch: true,
                page: 1,
                searchSortKey: sortKey,
                isApplyFilterClick: true,
                skipScroll: skipHistoryPush,
                preserveAppliedFilterSequence: true,
              })
            : fetchInventorisData({
                canonical_name: canonicalName,
                region: state.searchPageData.region,
                filters: filterObj,
                href,
                forceFetch: true,
                page: 1,
                searchSortKey: sortKey,
                isApplyFilterClick: true,
                skipScroll: skipHistoryPush,
              }),
        );

        // Push to history to enable back/forward navigation for filters.
        // Each filter change gets its own history entry so the user can
        // undo filters with the browser back button.
        if (!skipHistoryPush) {
          window.history.pushState({ filters, canonicalName }, "", newUrl);
        }
        // getSearchRequesting resets the order when the region is refetched — restore it.
        dispatch(setFilterApplicationOrder(applicationOrder));
      }
    } catch (error) {
      console.error("Error applying filter:", error);
      // Optionally dispatch error action
      dispatch({ type: "FILTER_APPLICATION_ERROR", payload: error });
    } finally {
      // Clear loading state
      if (skipHistoryPush) {
        dispatch({ type: "SET_FILTER_NAVIGATION_LOADING", payload: false });
      }
    }
  };
export const expandSearchRadius = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const { isRadiusExpanded, searchList, filters, region, canonical_name, radiusExpandEnabled } =
    state.searchPageData;

  if (!radiusExpandEnabled || isRadiusExpanded || searchList.readyStatus === "request") {
    return;
  }

  dispatch(setRadiusExpanded(true));
  // Nearby results change both the exhausted signal and the rendered-id set (FR-4).
  dispatch(resetRecommendations());

  const sortKey =
    searchList.params?.sort_key ||
    (region?.region_type === "establishment" || region?.region_type === "sublocality"
      ? "relevance_v0"
      : "relevance_sorting_v2");

  await dispatch(
    fetchInventorisData({
      canonical_name,
      region,
      filters: convertToFilterObj(filters),
      href: typeof window !== "undefined" ? window.location.href : "",
      forceFetch: false,
      skipScroll: true,
      appendResults: true,
      searchSortKey: sortKey,
      page: 1,
    }),
  );

  if (getState().searchPageData.searchList.readyStatus === "failure") {
    dispatch(setRadiusExpanded(false));
  }
};

export const clearFilter = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  dispatch(setRadiusExpanded(false));

  // Preserve locality=near-by-{region-name} when clearing filters
  // Check if locality includes "near-by" to match the region name pattern
  const currentLocality = state.searchPageData.filters?.locality;
  const preservedFilters =
    currentLocality && String(currentLocality).includes("near-by")
      ? { locality: currentLocality }
      : {};

  let newUrl = getSearchLink(state.searchPageData.canonical_name, preservedFilters);
  if (
    state.whitelabelData.isWhitelabel &&
    isDemoWhitelabelSubdomain(state.whitelabelData.subdomain)
  ) {
    newUrl = urlBuilder(newUrl, {
      params: { partner_id: state.whitelabelData.partner_uuid },
    });
  }

  let sortKey = "";
  // const searchSortingKey = getCookie("search_sorting_key_v2");
  // const searchSortingKey50 = getCookie("search_sorting_key_v2_50");
  // const regionId = state.searchPageData.region?.id;
  // const regionParentId = state.searchPageData.region?.parent_id;
  // const rootId =
  //   state?.searchPageData?.region?.ancestors_short?.length > 0
  //     ? state.searchPageData.region.ancestors_short.slice(-1)[0].id
  //     : "";
  // const sortingAlgoV2RegionIds = SORTING_ALGO_V2_REGION_IDS;
  // const sortingAlgoV2RegionIds50 = SORTING_ALGO_V2_REGION_IDS_50;

  // Check if region is enrolled in sorting algorithm experiment
  // const isInV2RegionIds =
  //   sortingAlgoV2RegionIds.includes(Number(regionId)) ||
  //   sortingAlgoV2RegionIds.includes(Number(regionParentId));
  // const isInV2RegionIds50 =
  //   sortingAlgoV2RegionIds50.includes(Number(regionId)) ||
  //   sortingAlgoV2RegionIds50.includes(Number(regionParentId));
  // const shouldApplyV2Sorting =
  //   (rootId == 1 || rootId == 5 || isInV2RegionIds50) && searchSortingKey50 === "TG1";

  // Check if this is a nearby page
  // Use includes to match locality=near-by-{region-name} pattern
  const isNearbyPage =
    preservedFilters.locality && String(preservedFilters.locality).includes("near-by");

  if (
    state.searchPageData.region?.region_type === "establishment" ||
    state.searchPageData.region?.region_type === "sublocality" ||
    ("locality" in state.searchPageData.filters && !isNearbyPage)
  ) {
    sortKey = "relevance_v0";
  } else if (isNearbyPage) {
    // For nearby page with no filters, use nearby_sorted_distance
    sortKey = "nearby_sorted_distance";
  } else {
    sortKey = "relevance_sorting_v2";
  }

  if (newUrl !== state.searchPageData.canonical_url) {
    window.scrollTo(0, 0);
    dispatch(setAppliedFilterSequence([]));
    dispatch(updateFilters(preservedFilters));
    dispatch(
      fetchInventorisData({
        canonical_name: state.searchPageData.canonical_name,
        filters: preservedFilters,
        href: window.location.href,
        forceFetch: true,
        region: state.searchPageData.region,
        searchSortKey: sortKey,
        page: 1,
      }),
    );
    // Push to history so clearing filters can be undone with back navigation.
    window.history.pushState(
      { filters: preservedFilters, canonicalName: state.searchPageData.canonical_name },
      "",
      newUrl,
    );
  }
};

const shouldFetchSearch = (state: AppState, canonical_name: string, urlFilters: any = {}) => {
  if (
    state.searchPageData.readyStatus === "invalid" ||
    state.searchPageData.canonical_name !== canonical_name
  ) {
    return true;
  }

  const currentHousingType = state.searchPageData.filters?.housingType || "all";
  const newHousingType = urlFilters?.housing_type || "all";

  // Check if locality changed to/from "near-by"
  // Use includes to match locality=near-by-{timestamp} pattern
  const currentLocality = state.searchPageData.filters?.locality;
  const newLocality = urlFilters?.locality;
  const currentIsNearby = currentLocality && String(currentLocality).includes("near-by");
  const newIsNearby = newLocality && String(newLocality).includes("near-by");
  const localityChanged = (currentIsNearby && !newIsNearby) || (!currentIsNearby && newIsNearby);

  return currentHousingType !== newHousingType || localityChanged;
};

export const fetchSearchDataIfNeed =
  (props: any, canonicalName = ""): AppThunk =>
  async (dispatch, getState: any) => {
    // GrowthBook is client-only; enable radius expand for SSR fetches so uni/locality direct
    // landings get X-Request-Source: global and the correct default radius behaviour.
    if (!__CLIENT__ && !config.IS_CHINA_APP) {
      dispatch(setRadiusExpandEnabled(true));
    }

    const stateForLang = getState();
    let selectedLang = stateForLang.appData.userLanguage || "en";
    // let searchSortingKey50 = "";
    if (!__CLIENT__) {
      if (config.IS_CHINA_APP) {
        selectedLang = stateForLang.appData.userLanguage || "zh";
      }

      // searchSortingKey50 =
      //   props?.req?.cookies?.search_sorting_key_v2_50 ||
      //   props?.req?.headers["x-feature-search_sorting_key_v2_50"] ||
      //   props?.req?.headers["X-Feature-Search_sorting_key_v2_50"];
    }
    const canonical_name =
      canonicalName || (__CLIENT__ ? props?.match?.params?.id : props?.params?.id);
    let filters: any = {};
    let user_flow_token = "";
    let user_auth_token = "";
    let initialWhitelabelFiltersSet = false;
    if (__CLIENT__) {
      const searchParams = new URLSearchParams(window?.location?.search);
      if (getState().router.action === "PUSH" && __CLIENT__) {
        window.scrollTo(0, 0);
      }

      for (const [key, value] of searchParams.entries()) {
        const isValidKey = filter_keys.includes(key);
        const isValidMoveIn = key === "move_in_month" ? isValidMoveInMonth(value) : true;

        if (isValidKey && isValidMoveIn) filters[key] = value;
      }
    } else {
      filters = props?.req?.query;
      // if sort_key and sort_order already present no need to add config listing preferences

      if (filters["move_in_month"] && !isValidMoveInMonth(filters["move_in_month"]))
        delete filters["move_in_month"];

      if (
        !filters?.sort_key &&
        !filters?.sort_order &&
        !filters?.region_radius &&
        getState().whitelabelData.isWhitelabelDataFetched
      ) {
        const [sort_key, sort_order] =
          getState().whitelabelData.config.listing.listing_preferences.split(",");
        const region_radius = getState().whitelabelData.config?.listing?.distance;
        if (sort_key !== "relevance_sorting_v2") {
          filters.sort_key = sort_key;
          filters.sort_order = sort_order;
          initialWhitelabelFiltersSet = true;
        }

        if (region_radius !== "20000") {
          filters.region_radius = region_radius;
          initialWhitelabelFiltersSet = true;
        }
      }
      user_flow_token = props?.req?.cookies?.user_flow_token || "";
      user_auth_token = props?.req?.cookies?.user_auth_token || "";
    }

    const href = (__CLIENT__ ? window.location.href : props.req?.url) || null;

    // Non-numeric values like "prev" or "next" resolve to page 1. only valid positive integers are accepted.
    if (filters?.p) {
      const parsedPage = parseInt(filters.p, 10);
      if (Number.isNaN(parsedPage) || parsedPage < 1) {
        delete filters.p;
      } else {
        filters.p = String(parsedPage);
      }
    }

    if (shouldFetchSearch(getState(), canonical_name, filters)) {
      const pageFromFilters = filters?.p ? parseInt(filters.p, 10) : 1;
      const result = await dispatch(
        fetchSearchData({
          canonical_name,
          href,
          staticContext: props.staticContext,
          filters,
          user_flow_token,
          user_auth_token,
          selectedLang,
          initialWhitelabelFiltersSet,
          page: pageFromFilters,
          reqCookies: props?.req?.cookies, // Pass request cookies for SSR
        }),
      );

      // Initialize history state on client side after first load
      if (__CLIENT__ && window.history.state === null) {
        const formattedFilters = getState().searchPageData.filters;
        window.history.replaceState(
          { filters: formattedFilters, canonicalName: canonical_name },
          "",
          window.location.href,
        );
      }

      return result;
    }
    const metaSEO = {
      ...getState().searchPageData.metaSEO,
      showZhDescription: selectedLang === "zh",
    };
    dispatch(getMetaSuccess(metaSEO));
    // Back from a property page: the list and the recommendation section survived in Redux,
    // so only the scroll position needs restoring (FR-32). Saved by the card on click.
    if (__CLIENT__ && typeof window.history.state?.scrollY === "number") {
      const { scrollY } = window.history.state;
      requestAnimationFrame(() => window.scrollTo({ top: scrollY }));
    }
    return null;
  };

export const fetchMoreInventoriesListIfNeed = (): AppThunk => (dispatch, getState) => {
  // If user arrived via a paginated URL (?p=N), skip infinite scroll loading
  if (__CLIENT__) {
    const searchParams = new URLSearchParams(window.location.search);
    const pageParam = searchParams.get("p");
    if (pageParam && parseInt(pageParam, 10) > 1) return null;
  }

  const state = getState();
  const nextPage = state?.searchPageData?.searchList?.next;
  const isInternalUser = state?.appData?.isInternalUser;
  const isAgentViewOpened = state?.searchPageData?.isAgentViewOpened;
  const customPropertyName = state?.searchPageData?.customPropertyName || "";
  const whiteLabelPartnerId = state?.searchPageData?.whiteLabelPartnerId || null;

  const params: any = {
    ...state?.searchPageData?.searchList?.params,
    p: nextPage,
  };
  params.methods = withActiveRoomTypesMethod(
    params.methods,
    hasRoomTypeFilter(params) || hasRoomTypeFilter(state?.searchPageData?.filters),
  );
  if (isInternalUser) {
    params.internal_user_monitoring = true;
  }
  if (isInternalUser && isAgentViewOpened) {
    params.active_children_with_filters = true;
    params.only = `${params.only},meta,owner`;
    if (customPropertyName) params.name = customPropertyName;
    if (whiteLabelPartnerId) params.whitelabel_partner_id = whiteLabelPartnerId;
    const agentViewZone = state?.searchPageData?.agentViewZone || 0;
    const agentViewStatuses = state?.searchPageData?.agentViewStatuses || "";
    if (agentViewZone) params.zone = agentViewZone;
    if (agentViewStatuses) params.statuses = agentViewStatuses;
  }
  if (nextPage && state.searchPageData?.searchList.page !== nextPage) {
    return dispatch(
      fetchMoreInventoriesList({
        params,
        nextPage,
        canonical_name: state?.searchPageData?.canonical_name,
        whitelabelData: state?.whitelabelData,
      }),
    );
  }
  return null;
};

const BATCH_SIZE = 10;

export const fetchAgentViewInventories = async ({
  filters = {},
  searchList = [],
  isInternalUser = false,
  region,
}: any) => {
  if (searchList.length === 0) return { result: [] };

  const buildOptions = (batchLength: number) => {
    const options: any = {
      params: {
        statuses: "active,inactive",
        sort_key: "relevance_sorting_v2",
        sort_order: "desc",
        active_children_with_filters: true,
        ...getFilterParams(convertToFilterObj(filters)),
        filter_available_rooms_tenancies: false,
        limit: batchLength,
        ...(isInternalUser && { internal_user_monitoring: true }),
        isInternalUser,
        use_v2_structure: true,
      },
      withCredentials: true,
    };

    if (options?.params?.range_available_price) {
      if (!COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name)) {
        options.params.weekly_range_price = options.params.range_available_price;
        options.params.duration = "weekly";
        delete options.params.range_available_price;
      } else {
        options.params.duration = "monthly";
      }
    }

    if (region?.canonical_name) {
      options.params.region_canonical_name = region?.canonical_name;
    }

    if (options.params.only) {
      options.params.only = `${options.params.only},owner,meta,provider_id,payment_types`;
    }
    if (options.params.methods) {
      options.params.methods = `${options.params.methods},reviews_rating,reviews_count,shortlink,active_offers,destination_distances,is_custom_form_enabled`;
    }

    if (options.params?.meta_types) {
      options.params = {
        ...options.params,
        room_types: [...ROOM_TYPE_SPECIFIC.list, ...ROOM_TYPE_FILTER.list]
          .flat()
          .map((it) => it.value)
          .filter((it) => metaTypesHas(options.params?.meta_types, it))
          .join(","),
        bathroom_types: SHARING_OPTIONS_BATHROOM.list
          .map((it) => it.value)
          .filter((it) => metaTypesHas(options.params?.meta_types, it))
          .join(","),
        kitchen_types: SHARING_OPTIONS_KITCHEN.list
          .map((it) => it.value)
          .filter((it) => metaTypesHas(options.params?.meta_types, it))
          .join(","),
      };

      delete options.params?.meta_types;
      if (!options.params.bathroom_types) {
        delete options.params.bathroom_types;
      }
      if (!options.params.kitchen_types) {
        delete options.params.kitchen_types;
      }
      if (!options.params.room_types) {
        delete options.params.room_types;
      }
    }

    return options;
  };

  // Split searchList into batches of BATCH_SIZE
  const batches: any[][] = [];
  for (let i = 0; i < searchList.length; i += BATCH_SIZE) {
    batches.push(searchList.slice(i, i + BATCH_SIZE));
  }

  // Fetch all batches in parallel
  const batchResults = await Promise.all(
    batches.map(async (batch) => {
      const ids = batch.map(({ id }: any) => id).join(",");
      const options = buildOptions(batch.length);
      options.headers = {
        ...(options.headers || {}),
        ...(config.IS_CHINA_APP ? {} : { "X-Use-Java-Api": "true" }),
      };
      options.params = {
        ...(options.params || {}),
        use_java_api: true,
      };
      const {
        data: { data },
      } = await axios(`${config.API_URL}/api/v1/inventories?ids=${ids}`, options);
      return data?.result || [];
    }),
  );

  // Flatten and return combined results
  return { result: batchResults.flat() };
};

export const fetchDistancesBulk = async (distanceRequests: any) => {
  try {
    const options = {
      request: distanceRequests,
    };

    const {
      data: { data },
    } = await axios.post(`${config.API_URL}/api/v0/regions/distances/fetch_bulk`, options);
    return data;
  } catch (error) {
    console.error("Error fetching distances bulk:", error);
    throw error;
  }
};

export const getRegionAutoSuggestGoogleAutoSuggest = async (inputValue: string) => {
  try {
    const options = {
      params: {
        types: "geocode|establishment",
        input: inputValue,
      },
    };
    const {
      data: { data },
    } = await axios(`${config.API_URL}/api/v0/regions/search_google_autosuggest`, options);
    return data;
  } catch (error) {
    console.error("Error fetching region auto suggest:", error);
    throw error;
  }
};

export const getWhiteLabelPartners = async (partnerName = "") => {
  try {
    const options: any = {
      params: {
        only: "id,name,whitelabel_domain_url,uuid",
        whitelabel_domain_url_status_in: "created,active",
      },
      withCredentials: true,
    };

    if (partnerName) {
      options.params["name"] = partnerName;
    }
    const {
      data: { data },
    } = await axios(`${config.API_URL}/leads/partners/search`, options);
    return data;
  } catch (error) {
    console.error("Error fetching white label partners:", error);
    throw error;
  }
};

/** Minimal shape rather than `AppState` — importing the store's type here would risk a cycle. */
type StateWithSearchPage = { searchPageData?: { dynamicFilterCountData?: any } };

/**
 * The aggregation payload in the filter-count response: slice -> axios response -> API body, each
 * a separate `data`. Read it here so an envelope change is one edit, not a silent undefined at
 * every call site.
 */
export const selectFilterCountPayload = (state: StateWithSearchPage) =>
  state?.searchPageData?.dynamicFilterCountData?.data?.data;

/** The price histogram aggregation, or undefined when the current filters matched nothing. */
export const selectPriceHistogram = (state: StateWithSearchPage) =>
  selectFilterCountPayload(state)?.price_histogram;
